// ABOUTME: Topbar callout slot for the latest active notice for the current customer/relation user.
// ABOUTME: Shows title; body appears in a PopoverModal on hover; severity overrides type colour; resolved state shows a check icon.

import { Box, useTheme } from "@mui/material";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useEditor, EditorContent } from "@tiptap/react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { tokens } from "../../../../theme";
import { ENV_TYPES } from "../../Sidebar";
import { useGetActiveMeldingenQuery } from "../../../../newapi/global/newsRootSlice";
import { useTypeColors } from "../../Modals/MCreateNotice/useTypeColors";
import { findMeldingType, getMeldingTypeLogo } from "../../Modals/MCreateNotice/meldingTypes";
import { coreExtensions } from "../../../theme/chat/extensions/editorExtensions";
import PopoverModal from "../../../theme/Modal/PopoverModal";
import Text from "../../../theme/text/Text";

const ReadOnlyBody = ({ content, color }) => {
    const editor = useEditor({
        extensions: coreExtensions,
        content: content || "",
        editable: false,
    });
    if (!editor) return null;
    return (
        <Box
            sx={{
                "& .ProseMirror": {
                    outline: "none",
                    fontSize: 13,
                    lineHeight: 1.4,
                    fontFamily: "Roboto, sans-serif",
                    color,
                    "& p": { margin: 0 },
                },
            }}
        >
            <EditorContent editor={editor} />
        </Box>
    );
};

const formatDateTime = (raw, locale) => {
    if (!raw) return "";
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(locale || "nl-NL", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const MeldingSlot = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t, i18n } = useTranslation();

    const token = useSelector((state) => state.auth.token);
    const envType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isReaderEnv = envType === ENV_TYPES[100] || envType === ENV_TYPES[200];
    const enabled = Boolean(token) && isReaderEnv;

    const { data: meldingen = [] } = useGetActiveMeldingenQuery(undefined, { skip: !enabled });
    const melding = meldingen[0];
    const { bg, bgTransparent, labelBg, softBg, color } = useTypeColors(melding?.type, theme.palette.mode, melding?.severity);

    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);

    if (!enabled || !melding) return null;

    const typeEntry = melding.type ? findMeldingType(melding.type) : null;
    const typeLogo = melding.type ? getMeldingTypeLogo(melding.type, 10) : null;
    const typeInitials = typeEntry ? typeEntry.label.slice(0, 2).toUpperCase() : "";
    const resolved = Boolean(melding.resolvedAt);
    const hasBody = Boolean(melding.body);

    const handleClick = () => {
        if (melding.url) {
            window.open(melding.url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <>
            <Box
                ref={anchorRef}
                onMouseEnter={hasBody ? () => setOpen(true) : undefined}
                onMouseLeave={hasBody ? () => setOpen(false) : undefined}
                onClick={melding.url ? handleClick : undefined}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pl: 0.5,
                    pr: 1.5,
                    py: 0.5,
                    borderRadius: 5,
                    background: bgTransparent,
                    color,
                    cursor: melding.url ? "pointer" : "default",
                    maxWidth: 420,
                    minHeight: 32,
                    fontFamily: "Roboto, sans-serif",
                    "&:hover": melding.url ? { opacity: 0.85 } : {},
                }}
            >
                {melding.type && (
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            background: bg,
                            color: colors.txt["light"],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            overflow: "hidden",
                            fontSize: 10,
                            fontWeight: 700,
                        }}
                    >
                        <Box
                            sx={{
                                width: 24,
                                height: 24,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": { width: "100%", height: "100%", display: "block" },
                            }}
                        >
                            {typeLogo || typeInitials}
                        </Box>
                    </Box>
                )}
                <Box
                    sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                        textDecoration: resolved ? "line-through" : "none",
                        opacity: resolved ? 0.7 : 1,
                    }}
                >
                    {melding.title || ""}
                </Box>
                {resolved && (
                    <CheckCircleOutlineIcon
                        sx={{ fontSize: 16, flexShrink: 0, color: colors.green[400] }}
                    />
                )}
                {melding.url && (
                    <OpenInNewIcon sx={{ fontSize: 14, flexShrink: 0, opacity: 0.7 }} />
                )}
            </Box>

            <PopoverModal
                id="melding-body-popover"
                open={open && hasBody}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                hover
                triangle={{ position: "center", color: bg }}
                disableRestoreFocus
                disableAutoFocus
                disableEnforceFocus
                disableScrollLock
            >
                <Box
                    sx={{
                        background: `linear-gradient(${bgTransparent}, ${bgTransparent}), ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0]}`,
                        color,
                        px: 2,
                        py: 1.5,
                        maxWidth: 420,
                        minWidth: 240,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {typeEntry && (
                        <Box
                            sx={{
                                fontSize: 14,
                                mb:3,
                                fontFamily: "Roboto, sans-serif",
                                fontWeight: 700,
                                background: labelBg,
                                color: softBg,
                                width:1,
                                height:32,
                                lineHeight:'32px',
                                borderRadius:4,
                                textAlign:'center',
                            }}
                        >
                            {typeEntry.label}
                        </Box>
                    )}

                    <ReadOnlyBody content={melding.body} color={color} />

                    {(melding.publishedAt || melding.resolvedAt) && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                mt:3,
                            }}
                        >
                            {melding.publishedAt && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        background: labelBg,
                                        color: softBg,
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: 14,
                                        fontWeight: 700,
                                        height:32,
                                        pl:2,
                                        borderRadius:4,
                                        pr:0.5,
                                    }}
                                >
                                    <Box>{t("published_at") || "Gepubliceerd op"}</Box>
                                    <Box
                                        sx={{
                                            background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                            color: colors.txt["primary"],
                                            borderRadius: 3,
                                            height:24,
                                            lineHeight:"24px",
                                            px:1,
                                        }}
                                    >
                                        <Text semibold>{formatDateTime(melding.publishedAt, i18n.language)}</Text>
                                    </Box>
                                </Box>
                            )}
                            {melding.resolvedAt && (
                                <Box
                                    sx={{
                                        height:32,
                                        borderRadius:4,
                                        background:colors.green[100],
                                        pl:2,
                                        pr:0.5,
                                        display:'flex',
                                        gap:2,
                                        alignItems:"center",
                                        justifyContent:"space-between",
                                    }}
                                >
                                    <Text variant="green" bold>{t("resolved_at") || "Opgelost op"}</Text>
                                    <Box
                                        sx={{
                                            background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                            color: colors.txt["primary"],
                                            borderRadius: 3,
                                            px:1,
                                            height:24,
                                            lineHeight:"24px",
                                        }}
                                    >
                                        <Text semibold>{formatDateTime(melding.resolvedAt, i18n.language)}</Text>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </PopoverModal>
        </>
    );
};

export default MeldingSlot;
